import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/icons";
import { TabTransition } from "@/components/tab-transition";
import { TAG_BASE_CLASS, TAG_SIZE_CLASSES, TAG_TONE_CLASSES } from "@/components/tag";
import { AreaChart } from "./area-chart";
import { EmptyInner } from "./empty-inner";
import { InfoHint } from "./info-hint";

export type ChartRange = "30d" | "12m";

export type ChartSeries = {
  data: number[];
  xLabels: string[];
  xStart: string;
  xEnd: string;
  unit: { one: string; other: string };
};

const RANGE_LABELS: Record<ChartRange, string> = {
  "30d": "Last 30 days",
  "12m": "Last 12 months",
};

const CHART_RANGES: ChartRange[] = ["30d", "12m"];

export function ChartCard({
  title,
  titleInfo,
  subtitle,
  series,
  defaultRange = "30d",
  empty = false,
  emptyTitle,
  emptyHint,
}: {
  title: string;
  /** Optional tooltip text shown via a circled "i" next to the title. */
  titleInfo?: string;
  subtitle: string;
  series: Record<ChartRange, ChartSeries>;
  defaultRange?: ChartRange;
  empty?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
}) {
  const [range, setRange] = useState<ChartRange>(defaultRange);
  const active = series[range];
  return (
    <article className="card flex flex-col gap-4">
      <header className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-1 text-black">
            <h3 className="text-body font-semibold">{title}</h3>
            {titleInfo && <InfoHint text={titleInfo} />}
          </div>
          <p className="text-small text-grey-600">{subtitle}</p>
        </div>
        {!empty && <RangeDropdown value={range} onChange={setRange} />}
      </header>
      <TabTransition transitionKey={range} skipInitial>
        {empty ? (
          <EmptyInner
            icon="show_chart"
            title={emptyTitle ?? "No data yet"}
            hint={emptyHint}
            minHeight={229}
          />
        ) : (
          <AreaChart
            data={active.data}
            xLabels={active.xLabels}
            xStart={active.xStart}
            xEnd={active.xEnd}
            unit={active.unit}
          />
        )}
      </TabTransition>
    </article>
  );
}

function RangeDropdown({
  value,
  onChange,
}: {
  value: ChartRange;
  onChange: (next: ChartRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (event.target instanceof Node && wrapperRef.current.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          TAG_BASE_CLASS,
          TAG_SIZE_CLASSES.sm,
          TAG_TONE_CLASSES.neutral,
          "transition-colors hover:bg-grey-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-300",
          open && "bg-grey-200",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <span>{RANGE_LABELS[value]}</span>
        <Icon name="keyboard_arrow_down" size={20} weight={300} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Chart range"
          className="absolute right-0 top-full z-20 mt-1 flex w-45 flex-col rounded-s, border border-grey-200 bg-white p-1 shadow-flat"
        >
          {CHART_RANGES.map((option) => {
            const selected = option === value;
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={[
                  "flex items-center gap-1 rounded-sm px-2 py-1.5 text-left text-small font-medium",
                  selected ? "bg-grey-100 text-black" : "text-grey-900 hover:bg-grey-50",
                ].join(" ")}
              >
                <span className="flex-1">{RANGE_LABELS[option]}</span>
                {selected && <Icon name="check_small" size={20} weight={300} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
